import { AnimatePresence, motion } from 'framer-motion'
import React from 'react'
import styled from 'styled-components'
import EntriesSelector from './EntriesSelector'
import BalanceCheckBar from './BalanceCheckBar'
import WorkflowContactField from './WorkflowContactField'
import { Input, RadioGroup } from '@root/components'
import { Controller, get, useFormContext, useWatch } from 'react-hook-form'
import { IAccountingRule } from '@root/query'
import { useTranslation } from 'react-i18next'
import { trapSpacesForRequiredFields } from '@root/utils'
import { TagSelector } from '@root/containers/TagSelector'
import { useBusinessContext } from '@root/context'
import { ACCRUAL_DATE_OPTIONS, isPurchaseInvoiceSource } from '../accrualDateMode'

type AttachmentPolicy = 'wait' | 'book' | 'exempt'

const attachmentPolicyFromRule = (
  waitForAttachment: boolean | undefined,
  attachmentExempt: boolean | undefined
): AttachmentPolicy => {
  if (waitForAttachment) return 'wait'
  if (attachmentExempt) return 'exempt'
  return 'book'
}

const attachmentPolicyToFlags = (
  policy: AttachmentPolicy
): { wait_for_attachment: boolean; attachment_exempt: boolean } => {
  switch (policy) {
    case 'wait':
      return { wait_for_attachment: true, attachment_exempt: false }
    case 'exempt':
      return { wait_for_attachment: false, attachment_exempt: true }
    case 'book':
    default:
      return { wait_for_attachment: false, attachment_exempt: false }
  }
}

interface Props {
  showWorkflowContact?: boolean
  readOnly?: boolean
}

const DocumentCreationSection: React.FC<Props> = ({
  showWorkflowContact = false,
  readOnly = false
}) => {
  const {
    register,
    control,
    formState: { errors }
  } = useFormContext<IAccountingRule>()

  const { data: business } = useBusinessContext()
  const [t] = useTranslation()
  const accrualDateMode = useWatch({ control, name: 'accrual_date_mode' })
  const showAccrualDate =
    !readOnly &&
    isPurchaseInvoiceSource(
      Boolean(business?.has_accrual_based_entries_for_invoicing),
      accrualDateMode
    )

  return (
    <StyledContainer>
      <AnimatePresence initial={false}>
        {showAccrualDate && (
          <DateReveal
            key="accrual-date"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <DateChoice>
              <FieldLabel>
                {t('settings.automation.accountingRules.form.accrualDate')}
                <RequiredMark> *</RequiredMark>
              </FieldLabel>
              <Controller
                control={control}
                name="accrual_date_mode"
                rules={{
                  required: { value: true, message: t('validation.required') }
                }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <RadioGroup
                    name="accrual_date_mode"
                    value={value ?? undefined}
                    onChange={next => onChange(next)}
                    errors={error ? { accrual_date_mode: error } : undefined}
                    options={ACCRUAL_DATE_OPTIONS.map(option => ({
                      value: option.value,
                      label: t(`settings.automation.accountingRules.form.${option.labelKey}`),
                      description: t(
                        `settings.automation.accountingRules.form.${option.labelKey}Description`
                      )
                    }))}
                  />
                )}
              />
            </DateChoice>
          </DateReveal>
        )}
      </AnimatePresence>
      <Input
        label={t('settings.automation.accountingRules.form.document.description')}
        placeholder={t(
          'settings.automation.accountingRules.form.document.entryDescriptionPlaceholder'
        )}
        {...register('description', {
          required: readOnly ? false : { value: true, message: t('validation.required') },
          validate: readOnly
            ? undefined
            : value => trapSpacesForRequiredFields(value, t('validation.required'))
        })}
        autoComplete="off"
        errors={get(errors, 'description')}
        required={!readOnly}
        disabled={readOnly}
      />

      {showWorkflowContact && <WorkflowContactField />}

      {business?.has_tags && (
        <StyledTagContainer>
          <TagsLabel>{t('settings.automation.accountingRules.form.document.tags')}</TagsLabel>
          <Controller
            name="tag_ids"
            control={control}
            render={({ field }) => <TagSelector {...field} />}
          />
        </StyledTagContainer>
      )}

      <EntriesSelector
        baseName={'blueprint.debet_entries'}
        header={t('settings.automation.accountingRules.form.document.debet')}
        buttonLabel={t('settings.automation.accountingRules.form.document.addDebitRow')}
      />

      <EntriesSelector
        baseName={'blueprint.credit_entries'}
        header={t('settings.automation.accountingRules.form.document.credit')}
        buttonLabel={t('settings.automation.accountingRules.form.document.addCreditRow')}
      />

      <BalanceCheckBar />
    </StyledContainer>
  )
}

export const AttachmentStep: React.FC<{ readOnly?: boolean }> = ({ readOnly = false }) => {
  const { control, setValue } = useFormContext<IAccountingRule>()
  const [t] = useTranslation()
  const waitForAttachment = useWatch({ control, name: 'wait_for_attachment' })
  const attachmentExempt = useWatch({ control, name: 'attachment_exempt' })
  const policy = attachmentPolicyFromRule(waitForAttachment, attachmentExempt)

  return (
    <RadioGroup
      name="attachment_policy"
      value={policy}
      disabled={readOnly}
      onChange={value => {
        const flags = attachmentPolicyToFlags(value as AttachmentPolicy)
        setValue('wait_for_attachment', flags.wait_for_attachment, { shouldDirty: true })
        setValue('attachment_exempt', flags.attachment_exempt, {
          shouldDirty: true
        })
      }}
      options={[
        {
          value: 'wait',
          label: t('settings.automation.accountingRules.form.document.waitForAttachment'),
          description: t(
            'settings.automation.accountingRules.form.document.waitForAttachmentDescription'
          )
        },
        {
          value: 'book',
          label: t('settings.automation.accountingRules.form.document.bookWithoutWaiting'),
          description: t(
            'settings.automation.accountingRules.form.document.bookWithoutWaitingDescription'
          )
        },
        {
          value: 'exempt',
          label: t('document.attachmentExempt'),
          description: t(
            'settings.automation.accountingRules.form.document.attachmentExemptDescription'
          )
        }
      ]}
    />
  )
}

export default DocumentCreationSection

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const DateReveal = styled(motion.div)`
  overflow: hidden;
`

const DateChoice = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const FieldLabel = styled.span`
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`

const RequiredMark = styled.span`
  color: ${({ theme }) => theme.colors.error};
`

const TagsLabel = styled.span`
  display: block;
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  margin-bottom: ${({ theme }) => theme.spacing.xxs}rem;
`

const StyledTagContainer = styled.div`
  label {
    margin-top: 0;
  }
`
